// Regras do agendamento de notificações do painel administrativo.
//
// Puro (sem firebase/RN) para ser testável: é esta lógica que decide se um
// horário escolhido pelo admin é válido, se ele conflita com outro envio já
// programado e como a fila é apresentada — o que permite montar as comunicações
// de um dia, uma semana ou um mês de uma vez só.
//
// FUSO: tudo aqui trabalha com `Date` no fuso LOCAL do navegador do admin.
// A data escolhida ("15/08" às "09:30") é convertida para um instante absoluto
// na hora de gravar, e o cron do backend compara instantes — então não existe
// conversão manual de fuso em lugar nenhum (era exatamente onde o lembrete de
// aulas se enrolava, com `getHours() + 3` espalhado pelo código).

// O cron de disparo roda a cada minuto. Aceitar um horário mais próximo que
// isso prometeria uma precisão que o backend não tem.
export const MIN_LEAD_MINUTES = 2;

// Janela usada só para AVISAR que existe outro envio por perto. Não bloqueia
// nada: agendar dois envios próximos é uma decisão legítima de comunicação, e o
// backend serializa a entrega numa fila — um sai depois do outro, nunca em
// paralelo. O aviso existe porque o aluno receber duas notificações seguidas
// costuma ser engano, e vale ser visto antes de confirmar.
export const NEARBY_WARNING_MINUTES = 15;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^(\d{1,2}):(\d{2})$/;

const MS_PER_MINUTE = 60000;

// Frequências de repetição, no espírito do calendário: um envio único ou uma
// série que se repete todo dia, toda semana (no mesmo dia da semana) ou todo
// mês (no mesmo dia do mês).
export const FREQUENCIES = ['once', 'daily', 'weekly', 'monthly'];

// ATENÇÃO: o cálculo da próxima ocorrência existe DUAS vezes no projeto —
// aqui, para a tela mostrar a prévia das próximas datas, e em
// `functions/services/scheduledNotifications.js`, que é quem de fato avança a
// série no disparo. Os dois pacotes não compartilham código (o backend é
// CommonJS e sai no deploy só com a pasta `functions/`), então as duas
// implementações seguem o mesmo algoritmo e as mesmas suítes de teste.
// Qualquer mudança de regra aqui precisa ser repetida lá.

// A próxima data de uma série, a partir de uma ocorrência.
//
// Mensal mantém o DIA DO MÊS e PULA os meses que não têm esse dia — é o
// comportamento do Google Calendar para "todo dia 31". A alternativa (encostar
// no último dia do mês) faria uma série do dia 31 aparecer no dia 28, numa data
// que o admin nunca escolheu.
export const nextOccurrence = (from, frequency) => {
  if (!(from instanceof Date) || Number.isNaN(from.getTime())) return null;

  const horas = from.getHours();
  const minutos = from.getMinutes();

  if (frequency === 'daily') {
    return new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1, horas, minutos, 0, 0);
  }

  if (frequency === 'weekly') {
    return new Date(from.getFullYear(), from.getMonth(), from.getDate() + 7, horas, minutos, 0, 0);
  }

  if (frequency === 'monthly') {
    const dia = from.getDate();
    let ano = from.getFullYear();
    let mes = from.getMonth();

    // No máximo 12 tentativas: todo dia de 1 a 31 existe em algum mês do ano.
    for (let i = 0; i < 12; i += 1) {
      mes += 1;
      if (mes > 11) {
        mes = 0;
        ano += 1;
      }
      const candidato = new Date(ano, mes, dia, horas, minutos, 0, 0);
      // `new Date(2026, 1, 31)` vira 03/03: dia diferente = mês sem esse dia.
      if (candidato.getDate() === dia) return candidato;
    }
  }

  return null; // 'once' e frequências desconhecidas não têm próxima data
};

// Teto de segurança do laço de recuperação. 800 passos cobrem mais de dois anos
// de uma série diária — o suficiente para qualquer indisponibilidade real, sem
// risco de laço infinito se `nextOccurrence` algum dia parar de avançar.
const MAX_CATCH_UP_STEPS = 800;

// A próxima ocorrência que ainda está no FUTURO, e o fim da série.
//
// Avançar um passo só não bastaria: se o disparo ficar fora do ar por uma
// semana, uma série diária teria seis datas vencidas na fila e mandaria seis
// notificações seguidas para o aluno, uma a cada rodada do cron. Pulando direto
// para a próxima data futura, um período fora do ar custa no máximo um envio.
//
// Devolve null quando a série acabou (passou da data de término).
export const advanceSchedule = ({ from, frequency, nowMs = Date.now(), untilMs = null }) => {
  let proxima = nextOccurrence(from, frequency);
  let passos = 0;

  while (proxima && proxima.getTime() <= nowMs && passos < MAX_CATCH_UP_STEPS) {
    proxima = nextOccurrence(proxima, frequency);
    passos += 1;
  }

  if (!proxima) return null;
  // `untilMs` é o fim do dia escolhido como término, montado por quem chama.
  if (untilMs !== null && proxima.getTime() > untilMs) return null;

  return proxima;
};

// As próximas `count` datas de uma série, para a tela mostrar no que o admin
// está se comprometendo antes de confirmar.
export const previewOccurrences = ({ start, frequency, untilMs = null, count = 3 }) => {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return [];
  if (frequency === 'once' || !frequency) return [start];

  const datas = [start];
  let atual = start;

  while (datas.length < count) {
    const proxima = nextOccurrence(atual, frequency);
    if (!proxima) break;
    if (untilMs !== null && proxima.getTime() > untilMs) break;
    datas.push(proxima);
    atual = proxima;
  }

  return datas;
};

// "Toda segunda-feira", "Todo sábado", "Todo dia 15 do mês" — o resumo da
// regra, montado a partir da data escolhida, como no calendário.
export const describeRecurrence = (frequency, date) => {
  if (frequency === 'daily') return 'Todo dia';

  if (frequency === 'weekly') {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'Toda semana';
    const diaSemana = date.toLocaleDateString('pt-BR', { weekday: 'long' });
    // Sábado e domingo são masculinos; os dias "-feira" são femininos.
    const artigo = diaSemana.includes('-feira') ? 'Toda' : 'Todo';
    return `${artigo} ${diaSemana}`;
  }

  if (frequency === 'monthly') {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'Todo mês';
    return `Todo dia ${date.getDate()} do mês`;
  }

  return 'Não se repete';
};

// Máscara de "HH:MM" enquanto o admin digita.
//
// Os dois-pontos que o PRÓPRIO admin digita são preservados: uma máscara só de
// dígitos transformava "9:30" (a forma mais natural de escrever) em "93:0",
// porque contava o 9 como a primeira das duas casas da hora. Quando não há
// dois-pontos, ele entra sozinho depois da segunda casa — assim "0930"
// também funciona.
export const maskTimeInput = (raw) => {
  const limpo = String(raw ?? '').replace(/[^\d:]/g, '');

  const separador = limpo.indexOf(':');
  if (separador >= 0) {
    const horas = limpo.slice(0, separador).replace(/\D/g, '').slice(0, 2);
    const minutos = limpo.slice(separador + 1).replace(/\D/g, '').slice(0, 2);
    return `${horas}:${minutos}`;
  }

  if (limpo.length <= 2) return limpo;
  return `${limpo.slice(0, 2)}:${limpo.slice(2, 4)}`;
};

// Arruma o campo quando o admin sai dele: "9:3" -> "09:30", "9" -> "09:00".
// Entrada que não dá para interpretar volta intacta, para o aviso de erro ser
// quem explica o problema (em vez de o campo "consertar" para um horário que o
// admin não escolheu).
export const normalizeTimeInput = (raw) => {
  const texto = String(raw ?? '').trim();
  if (!texto) return '';

  const match = /^(\d{1,2})(?::(\d{0,2}))?$/.exec(texto);
  if (!match) return texto;

  const horas = Number(match[1]);
  const minutos = Number(match[2] || 0);
  if (horas > 23 || minutos > 59) return texto;

  return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`;
};

// Junta a data do calendário ('YYYY-MM-DD') com a hora digitada ('HH:MM') num
// Date local. Devolve null quando qualquer um dos dois é inválido.
export const parseScheduleDateTime = (dateIso, timeText) => {
  if (!DATE_RE.test(String(dateIso ?? ''))) return null;

  const match = TIME_RE.exec(String(timeText ?? '').trim());
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  const [year, month, day] = dateIso.split('-').map(Number);
  const date = new Date(year, month - 1, day, hours, minutes, 0, 0);

  // `new Date(2026, 1, 31)` não estoura: vira 03/03. Sem esta conferência, uma
  // data inexistente seria aceita em silêncio e o envio sairia no dia errado.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

// O envio já programado mais próximo de `target`, dentro da janela de aviso.
// `existingDates` aceita nulos (docs sem data) — eles são ignorados.
export const findScheduleConflict = (target, existingDates, gapMinutes = NEARBY_WARNING_MINUTES) => {
  if (!(target instanceof Date)) return null;

  const gapMs = gapMinutes * MS_PER_MINUTE;

  return (
    (existingDates || []).find(
      (date) => date instanceof Date && Math.abs(date.getTime() - target.getTime()) < gapMs
    ) || null
  );
};

// Fim do dia ('YYYY-MM-DD' às 23:59:59.999 no fuso local) — o término da série
// é uma DATA, então uma ocorrência marcada para as 09:00 desse dia ainda conta.
export const endOfDayMs = (dateIso) => {
  if (!DATE_RE.test(String(dateIso ?? ''))) return null;
  const [ano, mes, dia] = dateIso.split('-').map(Number);
  return new Date(ano, mes - 1, dia, 23, 59, 59, 999).getTime();
};

// Valida o horário escolhido. Devolve `{ date }` no sucesso e `{ error }` com a
// mensagem já em PT-BR — a tela só precisa exibir.
//
// `frequency`/`untilIso` só importam para séries: a data de término precisa
// alcançar pelo menos a primeira ocorrência, senão o agendamento nasceria
// vencido e nada seria enviado.
export const validateSchedule = ({
  dateIso,
  timeText,
  now = new Date(),
  frequency = 'once',
  untilIso = '',
}) => {
  if (!dateIso) return { error: 'Escolha a data do envio.' };
  if (!String(timeText ?? '').trim()) return { error: 'Informe o horário do envio.' };

  const date = parseScheduleDateTime(dateIso, timeText);
  if (!date) return { error: 'Data ou horário inválidos. Use o formato HH:MM (ex.: 09:30).' };

  const leadMs = MIN_LEAD_MINUTES * MS_PER_MINUTE;
  if (date.getTime() - now.getTime() < leadMs) {
    return {
      error: `O horário precisa estar pelo menos ${MIN_LEAD_MINUTES} minutos à frente de agora.`,
    };
  }

  if (frequency !== 'once' && untilIso) {
    const untilMs = endOfDayMs(untilIso);
    if (untilMs === null) return { error: 'Data de término inválida.' };
    if (untilMs < date.getTime()) {
      return { error: 'A repetição termina antes do primeiro envio. Escolha uma data posterior.' };
    }
  }

  return { date };
};

// Aviso, nunca bloqueio: só informa que existe outro envio por perto do horário
// escolhido. Devolve '' quando não há nada a dizer.
export const describeNearbySend = (date, scheduledDates) => {
  const perto = findScheduleConflict(date, scheduledDates);
  if (!perto) return '';

  // A lista mistura agendamentos futuros e envios já feitos, então o texto fala
  // em "envio" — dizer "notificação programada" seria errado para o segundo caso.
  return `Já existe um envio em ${formatScheduleDateTime(perto)}. Os dois serão entregues, um após o outro — confira se é isso mesmo.`;
};

// "15/08/2026 às 09:30".
export const formatScheduleDateTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

// Quanto falta para o disparo, em texto curto. Um agendamento vencido que ainda
// não saiu (cron atrasado) diz isso em vez de mostrar tempo negativo.
export const describeTimeUntil = (date, now = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  const diffMinutes = Math.round((date.getTime() - now.getTime()) / MS_PER_MINUTE);
  if (diffMinutes <= 0) return 'saindo agora';
  if (diffMinutes < 60) return `em ${diffMinutes} min`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `em ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;

  const diffDays = Math.floor(diffHours / 24);
  return `em ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`;
};

// Chave 'YYYY-MM-DD' do dia LOCAL — `toISOString()` é UTC e, à noite no Brasil,
// jogaria o agendamento para o dia seguinte.
const dayKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

// "Hoje", "Amanhã" ou "sexta-feira, 15/08" — o cabeçalho de cada dia da fila.
export const describeDay = (date, now = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';

  const hoje = dayKey(now);
  const amanha = dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  const alvo = dayKey(date);

  if (alvo === hoje) return 'Hoje';
  if (alvo === amanha) return 'Amanhã';

  const diaSemana = date.toLocaleDateString('pt-BR', { weekday: 'long' });
  const diaMes = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${diaSemana}, ${diaMes}`;
};

// Agrupa a fila por dia, em ordem cronológica — é o que dá a leitura de
// "as comunicações da semana" numa tela só. Itens sem data válida são
// descartados (doc corrompido não derruba a lista inteira).
export const groupByDay = (items, getDate, now = new Date()) => {
  const grupos = new Map();

  for (const item of items || []) {
    const date = getDate(item);
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) continue;

    const key = dayKey(date);
    if (!grupos.has(key)) grupos.set(key, { key, label: describeDay(date, now), items: [] });
    grupos.get(key).items.push({ item, date });
  }

  return Array.from(grupos.values())
    .sort((a, b) => (a.key < b.key ? -1 : 1))
    .map((grupo) => ({
      ...grupo,
      items: grupo.items.sort((a, b) => a.date - b.date).map((entry) => entry.item),
    }));
};
